<template>
  <q-table
    :columns="cols"
    :rows="rows"
    class="external-dns-table"
    :class="{ empty: rows.length === 0 }"
    ref="externalDnsTable"
    row-key="id"
    flat
    v-model:pagination="pagination"
    :loading="loading"
    binary-state-sort
    :rows-per-page-options="perPageOptions"
    @request="load"
    :expanded="expanded"
  >
    <template v-slot:no-data>
      <div class="flex full-width column items-center q-py-lg">
        <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
        <span class="block" style="font-size: 14px">No items were found.</span>
      </div>
    </template>

    <template v-slot:body="props">
      <q-tr :props="props">
        <q-td v-for="col in props.cols" :key="col.name" :props="props" :align="col.align">
          <div v-if="col.name === 'status'">
            <StatusCell v-if="!props.row.deleted_at" :status="col.value" />
            <q-icon v-else name="close" color="grey" size="15px"></q-icon>
          </div>
          <div v-else-if="col.name === 'value'">
            <span @click="copyValue(props.row.value)" class="long-descr cursor-pointer"
              >{{ props.row.value }}
              <q-tooltip
                ><span v-if="props.row.value.length > 22">{{ props.row.value }}<br /></span>Click to copy</q-tooltip
              ></span
            >
          </div>
          <div v-else-if="col.name === 'digitalService'">
            <router-link :to="`/my-portfolio/${props.row.registration?.digitalService?.apmId}`">{{
              props.row.registration?.digitalService?.apmId
            }}</router-link>
          </div>
          <div v-else-if="col.name === 'changeLink'">
            <a v-if="props.row.last_change_link" target="_blank" :href="props.row.last_change_link">{{
              props.row.last_change_number || 'Change Request'
            }}</a>
            <span v-else>-</span>
          </div>
          <ActionsCell
            v-else-if="col.name === 'actions'"
            @edit="$emit('toggleExpanded', props.row)"
            @remove="$emit('confirmDeleteDnsRecord', props.row)"
            @showWorkflow="showWorkflow($event, props.row)"
            @reassign="$emit('reassign', props.row)"
            :record="props.row"
            :disableEditDnsIds="disableEditDnsIds"
            :disableReassignDnsIds="disableReassignDnsIds"
            :loading="loading"
            :mode="mode"
            :disableDeleteDnsIds="disableDeleteDnsIds"
            :relatedWorkflowStatuses="relatedWorkflowStatuses"
          />
          <div v-else>{{ col.value }}</div>
        </q-td>
      </q-tr>
      <q-tr v-if="expanded?.length ? props.row.id === expanded[0] : false" :props="props"
        ><slot name="expand"></slot
      ></q-tr>
    </template>
  </q-table>
</template>

<script>
import { copyToClipboard } from 'quasar';
import dayjs from 'dayjs';
import { getDnsRecords } from '@/pages/allItems/api/api';
import { getRelatedWorkflowsStatuses } from '@/pages/registrations/api/api';
import { validateWorkFlow, subValWorkflow } from '@/pages/registrations/api/api';
import StatusCell from '@/pages/registrations/components/registration-network/components/StatusCell.vue';
import ActionsCell from '@/pages/registrations/components/registration-network/components/ActionsCell.vue';
import { supportCntrlClick } from '../helpers';

export default {
  name: 'DnsRecordsTable',
  components: {
    StatusCell,
    ActionsCell,
  },
  props: {
    rowsPerPageOptions: {
      type: Array,
      default: () => [5, 7, 10, 15, 20],
    },
    filters: {
      type: Object,
    },
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
    expanded: {
      type: Array,
    },
    mode: {
      type: String,
    },
  },
  data() {
    return {
      perPageOptions: this.rowsPerPageOptions,
      relatedWorkflowStatuses: [],
      rows: [],
      loading: false,
      pagination: {
        sortBy: 'created_at',
        descending: true,
        page: 1,
        rowsPerPage: this.mode === 'list' ? 20 : 5,
      },
      baseCols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Scope',
          name: 'scope',
          field: 'scope',
          align: 'left',
        },
        {
          label: 'Type',
          name: 'type',
          field: 'type',
          align: 'left',
        },
        {
          label: 'Name',
          name: 'name',
          field: 'name',
          align: 'left',
        },
        {
          label: 'Domain',
          name: 'domain',
          field: 'domain',
          align: 'left',
        },
        {
          label: 'Value',
          name: 'value',
          field: 'value',
          align: 'left',
        },
        {
          label: 'Change Request',
          name: 'changeLink',
          field: 'changeLink',
          align: 'center',
        },
        {
          label: 'Digital Service',
          name: 'digitalService',
          field: 'digitalService',
          align: 'left',
        },
        {
          label: 'Created at',
          name: 'created_at',
          field: 'created_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updated_at',
          field: 'updated_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
      disableDeleteDnsIds: [],
      disableReassignDnsIds: [],
      disableEditDnsIds: [],
    };
  },
  computed: {
    cols() {
      if (this.mode !== 'list') {
        return this.baseCols.filter((col) => col.name !== 'digitalService');
      }
      return this.baseCols;
    },
  },
  methods: {
    copyValue(value) {
      copyToClipboard(value).then(() => {
        this.$notify('info', 'Info', 'Value was copied');
      });
    },
    showWorkflow(event, record) {
      supportCntrlClick(event, `/workflows?itemId=${record.id}`);
    },
    async load(props) {
      this.loading = true;
      try {
        this.relatedWorkflowStatuses = [];
        this.disableDeleteDnsIds = [];
        this.disableReassignDnsIds = [];
        this.disableEditDnsIds = [];
        const { sortBy, descending, page, rowsPerPage } = props.pagination;
        const skip = page === 1 ? 0 : (page - 1) * rowsPerPage;
        const { count, data } = await getDnsRecords(
          rowsPerPage,
          skip,
          sortBy,
          descending,
          this.filters.scope,
          this.filters.id,
          this.filters.registrationId,
          this.filters.digitalService,
          this.filters.infraService,
          this.filters.changeNumber,
          this.filters.status,
          this.filters.name,
          this.filters.value,
          this.filters.domain,
          this.filters.type,
          this.filters.dateFrom,
          this.filters.dateTo,
        );
        this.pagination = { ...props.pagination, rowsNumber: count };
        if (this.mode === 'list') {
          this.$router.replace({ query: this.buildQueryFromFilters() });
        }
        this.rows = data;
        if (this.mode === 'edit' && this.filters.status !== 'removed') {
          const deletePrevalPromises = this.rows.map((record) => {
            const data = {
              workflowId: `engprt.delete-network-${record.scope}-dns`,
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id: record.id,
              },
            };
            return validateWorkFlow(data).catch((res) => {
              this.disableDeleteDnsIds.push({ id: record.id, message: res.message });
            });
          });
          const movePrevalPromises = this.rows.map((record) => {
            const { id } = record;
            const data = {
              workflowId: `engprt.move-network-${record.scope}-dns`,
              subSchemaId: 'move-button',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id,
              },
            };
            return subValWorkflow(data).catch((res) => {
              this.disableReassignDnsIds.push({ id: data.inputVariables.id, message: res.message });
            });
          });
          const editDnsPrevalPromises = this.rows.map((record) => {
            const { id, name, type, domain, value } = record;
            const data = {
              workflowId: `engprt.update-network-${record.scope}-dns`,
              subSchemaId: 'edit-button',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id,
                name,
                type,
                domain,
                value,
              },
            };
            return subValWorkflow(data).catch((res) => {
              this.disableEditDnsIds.push({ id: data.inputVariables.id, message: res.message });
            });
          });
          await Promise.all([...editDnsPrevalPromises, ...deletePrevalPromises, ...movePrevalPromises]);
        }
        const promises = this.rows.map((record) =>
          getRelatedWorkflowsStatuses(record.id).then((res) => {
            this.relatedWorkflowStatuses.push({ id: record.id, statuses: res });
          }),
        );
        await Promise.all(promises);
      } catch (err) {
        this.$notify('negative', 'Error', err.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
    reload() {
      this.$refs.externalDnsTable.requestServerInteraction();
    },
  },
  mounted() {
    if (this.mode === 'list') {
      const query = this.$route.query;
      const sortFields = ['created_at', 'updated_at'];

      for (const field of sortFields) {
        if (query?.[field]) {
          this.pagination.sortBy = field;
          this.pagination.descending = query[field] !== 'ASC';
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.long-descr {
  max-width: 150px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
